/**
 * Wilf's book as rules (pure: no engine, no DOM; unit-tested in trade.test.ts). The trade system
 * (tradeSystem.ts) owns the clock — seconds of play from the save — the cash, the car you are in and the law;
 * everything here is a function of those.
 *
 *  - Where goods are: your pockets (lost when you're nicked or laid out), the boot of a car (lost with the car,
 *    or when you're nicked in it), the stash in Lockup 7 (safe). `paid` is the average price per unit of
 *    everything you hold, so a sale shows ▲ / ▼ against it.
 *  - Prices are the shared online maths (shared/economy.ts): a door's price rises as its lot sells; a buyer's
 *    falls as the district fills up and as the law takes an interest (each sale adds district heat).
 *  - The hourly limit: profit above `TRADE.hourCap` in the last hour of play pays a quarter of the margin.
 *  - Lenny's tips: a buyer paying over the odds, a cheap extra lot, the Vanes' runner on its round. Each has
 *    an end time; the system posts them to the book and marks them on the map.
 */
import { BUYER_TYPES, MARKET_TUNING, decay, quotePurchase, quoteSale, type EconDistrictId } from '../../shared/economy';
import type { Rng } from '../core/rng';
import {
  GOOD_LABEL,
  HOLDALL_EXTRA,
  POCKETS,
  STASH_CAPACITY,
  TRADE,
  TRADE_GOODS,
  TRADE_PLACES,
  goodMaxOnFoot,
  goodSize,
  isTradeGood,
  tradePlace,
  type Goods,
  type TradeGood,
  type TradePlace,
} from './tradeData';

export interface Tip {
  id: string;
  kind: 'buyer' | 'lot' | 'runner';
  /** Trade place id (buyer / lot); the runner's first stop POI for a runner tip. */
  place: string;
  good?: TradeGood;
  /** Price multiplier: > 1 at a buyer, < 1 for a lot. */
  mul: number;
  /** Extra units on a lot tip, and how many have gone. */
  qty: number;
  used: number;
  from: number;
  until: number;
}

/** Prices last heard at a place (opening its card): unit prices, first unit. */
export interface Heard {
  t: number;
  buy: Goods;
  sell: Goods;
}

export interface TradeStats {
  deals: number;
  /** Net profit over all sales (can dip below zero). */
  profit: number;
  /** Best single sale's profit. */
  best: number;
  /** Units the law (or a wreck) took. */
  lost: number;
  /** The Vanes' runner: vans opened. */
  runners: number;
}

/** What the save keeps (`SaveData.trade`). Boots are folded into `boots` on write (the cars don't survive a reload). */
export interface TradeSave {
  pockets: Goods;
  stash: Goods;
  /** Goods in boots when the game was saved. On load they go to the stash if you have it, else your pockets. */
  boots: Goods;
  paid: Goods;
  holdall: boolean;
  /** [play second, profit] of recent sales (the hourly limit). */
  ledger: [number, number][];
  doors: Record<string, { since: number; sold: Goods }>;
  heard: Record<string, Heard>;
  tips: Tip[];
  nextTip: number;
  seq: number;
  lastRunner: number;
  stats: TradeStats;
}

export function emptyTrade(): TradeSave {
  return { pockets: {}, stash: {}, boots: {}, paid: {}, holdall: false, ledger: [], doors: {}, heard: {}, tips: [], nextTip: -1, seq: 0, lastRunner: -1e9, stats: { deals: 0, profit: 0, best: 0, lost: 0, runners: 0 } };
}

type Json = Record<string, unknown>;
const isObj = (v: unknown): v is Json => typeof v === 'object' && v !== null && !Array.isArray(v);
const fin = (v: unknown, d: number, lo = -1e12, hi = 1e12): number => (typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : d);

function cleanGoods(raw: unknown, max = 999): Goods {
  const out: Goods = {};
  if (!isObj(raw)) return out;
  for (const g of TRADE_GOODS) {
    const n = Math.round(fin(raw[g], 0, 0, max));
    if (n > 0) out[g] = n;
  }
  return out;
}

/** Shape-only clean-up of a saved trade record (never throws). */
export function sanitizeTrade(raw: unknown): TradeSave {
  const d = emptyTrade();
  if (!isObj(raw)) return d;
  const paid: Goods = {};
  if (isObj(raw.paid)) for (const g of TRADE_GOODS) if (typeof raw.paid[g] === 'number' && Number.isFinite(raw.paid[g])) paid[g] = Math.max(0, Math.min(10_000, raw.paid[g] as number));
  const ledger = Array.isArray(raw.ledger)
    ? raw.ledger.filter((e): e is [number, number] => Array.isArray(e) && e.length === 2 && e.every((x) => typeof x === 'number' && Number.isFinite(x))).slice(-200)
    : [];
  const doors: TradeSave['doors'] = {};
  if (isObj(raw.doors)) for (const p of TRADE_PLACES) {
    const r = raw.doors[p.id];
    if (p.door && isObj(r)) doors[p.id] = { since: fin(r.since, 0), sold: cleanGoods(r.sold) };
  }
  const heard: TradeSave['heard'] = {};
  if (isObj(raw.heard)) for (const p of TRADE_PLACES) {
    const r = raw.heard[p.id];
    if (isObj(r)) heard[p.id] = { t: fin(r.t, 0), buy: cleanGoods(r.buy, 100_000), sell: cleanGoods(r.sell, 100_000) };
  }
  const tips: Tip[] = Array.isArray(raw.tips)
    ? raw.tips
        .filter(isObj)
        .slice(0, 6)
        .flatMap((t): Tip[] => {
          const kind = t.kind === 'buyer' || t.kind === 'lot' || t.kind === 'runner' ? t.kind : null;
          if (!kind || typeof t.id !== 'string' || typeof t.place !== 'string') return [];
          const good = typeof t.good === 'string' && isTradeGood(t.good) ? t.good : undefined;
          return [{ id: t.id.slice(0, 40), kind, place: t.place.slice(0, 40), ...(good ? { good } : {}), mul: fin(t.mul, 1, 0.3, 2), qty: Math.round(fin(t.qty, 0, 0, 99)), used: Math.round(fin(t.used, 0, 0, 99)), from: fin(t.from, 0), until: fin(t.until, 0) }];
        })
    : [];
  const st = isObj(raw.stats) ? raw.stats : {};
  return {
    pockets: cleanGoods(raw.pockets),
    stash: cleanGoods(raw.stash),
    boots: cleanGoods(raw.boots),
    paid,
    holdall: raw.holdall === true,
    ledger,
    doors,
    heard,
    tips,
    nextTip: fin(raw.nextTip, -1),
    seq: Math.round(fin(raw.seq, 0, 0, 1e9)),
    lastRunner: fin(raw.lastRunner, -1e9),
    stats: {
      deals: Math.round(fin(st.deals, 0, 0)),
      profit: Math.round(fin(st.profit, 0)),
      best: Math.round(fin(st.best, 0, 0)),
      lost: Math.round(fin(st.lost, 0, 0)),
      runners: Math.round(fin(st.runners, 0, 0)),
    },
  };
}

// ------------------------------------------------------------------------------------------- helpers

export const units = (g: Goods): number => TRADE_GOODS.reduce((n, k) => n + (g[k] ?? 0) * goodSize(k), 0);
export const count = (g: Goods): number => TRADE_GOODS.reduce((n, k) => n + (g[k] ?? 0), 0);
export const isEmpty = (g: Goods): boolean => count(g) === 0;

function add(g: Goods, k: TradeGood, n: number): void {
  const v = (g[k] ?? 0) + n;
  if (v > 0) g[k] = v;
  else delete g[k];
}

/** Move every good from `from` into `to` (as far as `room` units allow); returns what moved. */
function pour(from: Goods, to: Goods, room = Infinity): Goods {
  const moved: Goods = {};
  let left = room;
  for (const k of TRADE_GOODS) {
    const have = from[k] ?? 0;
    const n = Math.min(have, Math.floor(left / goodSize(k)));
    if (n <= 0) continue;
    add(from, k, -n);
    add(to, k, n);
    add(moved, k, n);
    left -= n * goodSize(k);
  }
  return moved;
}

/** The car you are in (the system passes it each call): its id and boot size. */
export interface CarRef {
  id: number;
  name: string;
  capacity: number;
}

export interface Quote {
  ok: boolean;
  total: number;
  /** First unit's price. */
  unit: number;
  qty: number;
  /** Why not (plain). */
  reason?: string;
}

export interface SaleQuote extends Quote {
  /** Against what you paid: total − paid × qty. */
  profit: number;
  /** Taken off by the hourly limit. */
  capped: number;
  /** Deal heat (shared formula) and what it means. */
  heat: number;
  risk: Risk;
  tip: Tip | null;
}

export interface Risk {
  chance: number;
  level: 0 | 1 | 2;
  label: 'none' | 'low' | 'fair' | 'high';
}

/** A deal's chance of being clocked by the law, from its heat (shared `quoteSale().heat`). */
export function dealRisk(heat: number): Risk {
  if (heat < TRADE.riskFloor) return { chance: 0, level: 0, label: 'none' };
  const chance = Math.min(TRADE.riskMax, (heat - 2) / TRADE.riskSpan);
  return { chance, level: heat >= TRADE.level2Heat ? 2 : 1, label: chance < 0.2 ? 'low' : chance < 0.45 ? 'fair' : 'high' };
}

export const RISK_TEXT: Record<Risk['label'], string> = {
  none: 'No risk: too small to notice.',
  low: 'Small risk: the law might notice.',
  fair: 'Some risk: the law may well notice.',
  high: 'High risk: a deal this size gets noticed.',
};

/** Prices and pressure that fade with play time (not saved: ten quiet minutes and the borough has forgotten). */
export class Market {
  readonly demand = new Map<string, number>();
  readonly heat = new Map<EconDistrictId, number>();

  pressure(d: EconDistrictId, g: TradeGood): number {
    return this.demand.get(`${d}|${g}`) ?? 0;
  }

  sold(d: EconDistrictId, g: TradeGood, qty: number, heat: number): void {
    this.demand.set(`${d}|${g}`, this.pressure(d, g) + qty);
    this.heat.set(d, (this.heat.get(d) ?? 0) + heat);
  }

  advance(dtS: number): void {
    const ms = dtS * 1000;
    for (const [k, v] of this.demand) {
      const n = decay(v, ms, MARKET_TUNING.demandRecoveryMs);
      if (n <= 0) this.demand.delete(k);
      else this.demand.set(k, n);
    }
    for (const [k, v] of this.heat) {
      const n = decay(v, ms, MARKET_TUNING.districtHeatDecayMs);
      if (n <= 0) this.heat.delete(k);
      else this.heat.set(k, n);
    }
  }

  clear(): void {
    this.demand.clear();
    this.heat.clear();
  }
}

// ------------------------------------------------------------------------------------------- the trader

/**
 * The trade state in play: the saved record, the boots of the cars you've loaded (runtime: car id → goods) and
 * the market. Every method takes `now` (seconds of play) and, where it matters, the car you are in.
 */
export class Trader {
  readonly boots = new Map<number, Goods>();
  readonly market = new Market();

  constructor(public s: TradeSave = emptyTrade()) {}

  /** Load a saved record: boots from the last session go to the stash (if you have it) or your pockets. */
  load(s: TradeSave, hasStash: boolean): void {
    this.s = s;
    this.boots.clear();
    this.market.clear();
    if (!isEmpty(s.boots)) pour(s.boots, hasStash ? s.stash : s.pockets);
    s.boots = {};
  }

  /** The record to save (boots folded in). */
  snapshot(): TradeSave {
    const boots: Goods = {};
    for (const b of this.boots.values()) for (const k of TRADE_GOODS) if (b[k]) add(boots, k, b[k]!);
    return { ...this.s, boots };
  }

  pocketCap(holdall: boolean): number {
    return POCKETS + (holdall ? HOLDALL_EXTRA : 0);
  }

  boot(car: CarRef | null): Goods | null {
    if (!car) return null;
    let b = this.boots.get(car.id);
    if (!b) {
      b = {};
      this.boots.set(car.id, b);
    }
    return b;
  }

  /** Everything you hold of a good, wherever it is. */
  held(g: TradeGood): number {
    let n = (this.s.pockets[g] ?? 0) + (this.s.stash[g] ?? 0);
    for (const b of this.boots.values()) n += b[g] ?? 0;
    return n;
  }

  /** With you right now: pockets + the boot of the car you're in. */
  carried(g: TradeGood, car: CarRef | null): number {
    return (this.s.pockets[g] ?? 0) + (car ? (this.boots.get(car.id)?.[g] ?? 0) : 0);
  }

  /** How many more of `g` fit where a purchase goes (the boot if you're in a car, else your pockets). */
  room(g: TradeGood, car: CarRef | null, holdall: boolean): number {
    const size = goodSize(g);
    if (car) return Math.max(0, Math.floor((car.capacity - units(this.boot(car)!)) / size));
    const byUnits = Math.floor((this.pocketCap(holdall) - units(this.s.pockets)) / size);
    const byHands = goodMaxOnFoot(g) - (this.s.pockets[g] ?? 0);
    return Math.max(0, Math.min(byUnits, byHands));
  }

  /** Profit taken in the last hour of play (the limit's meter). */
  capUsed(now: number): number {
    let n = 0;
    for (const [t, p] of this.s.ledger) if (t > now - TRADE.windowS && p > 0) n += p;
    return n;
  }

  /** Seconds until the hour's limit has room again (0 when it has room now). */
  capFreesIn(now: number): number {
    if (this.capUsed(now) < TRADE.hourCap) return 0;
    const first = this.s.ledger.find(([t, p]) => t > now - TRADE.windowS && p > 0);
    return first ? Math.max(0, Math.ceil(first[0] + TRADE.windowS - now)) : 0;
  }

  // ----------------------------------------------------------------------------------------- tips

  /** Tips still running at `now` (expired ones are dropped from the record). */
  liveTips(now: number): Tip[] {
    this.s.tips = this.s.tips.filter((t) => t.until > now && !(t.kind === 'lot' && t.used >= t.qty));
    return this.s.tips;
  }

  private tipAt(kind: Tip['kind'], place: string, good: TradeGood, now: number): Tip | null {
    return this.liveTips(now).find((t) => t.kind === kind && t.place === place && t.good === good) ?? null;
  }

  /**
   * Lenny's next tip, when it's time (`now` ≥ `nextTip`) and fewer than `maxTips` are open. A runner tip only
   * when `runnerOk` (the system can put the van on the road) and the last one was long enough ago.
   */
  nextTip(now: number, rng: Rng, runnerOk: boolean): Tip | null {
    if (this.s.nextTip < 0) {
      this.s.nextTip = now + TRADE.firstTipS;
      return null;
    }
    if (now < this.s.nextTip) return null;
    this.s.nextTip = now + rng.range(TRADE.tipGapS[0], TRADE.tipGapS[1]);
    const live = this.liveTips(now);
    if (live.length >= TRADE.maxTips) return null;
    const id = `tip-${++this.s.seq}`;
    let tip: Tip;
    const runnerOut = live.some((t) => t.kind === 'runner');
    if (runnerOk && !runnerOut && now - this.s.lastRunner >= TRADE.runnerGapS && rng.chance(TRADE.runnerChance)) {
      this.s.lastRunner = now;
      tip = { id, kind: 'runner', place: 'runner', mul: 1, qty: 0, used: 0, from: now, until: now + TRADE.runnerS };
    } else if (rng.chance(0.6)) {
      const buyers = TRADE_PLACES.filter((p) => p.buyer && !live.some((t) => t.place === p.id));
      const p = rng.pick(buyers);
      const good = rng.pick(p.buyer!.accepts);
      const mul = Math.round(rng.range(TRADE.buyerTipMul[0], TRADE.buyerTipMul[1]) * 20) / 20;
      tip = { id, kind: 'buyer', place: p.id, good, mul, qty: 0, used: 0, from: now, until: now + TRADE.buyerTipS };
    } else {
      const doors = TRADE_PLACES.filter((p) => p.door && !live.some((t) => t.place === p.id));
      const p = rng.pick(doors);
      const goods = Object.keys(p.door!) as TradeGood[];
      const good = rng.pick(goods);
      tip = { id, kind: 'lot', place: p.id, good, mul: TRADE.lotTipMul, qty: Math.max(2, p.door![good]!.lot), used: 0, from: now, until: now + TRADE.lotTipS };
    }
    this.s.tips.push(tip);
    return tip;
  }

  // ----------------------------------------------------------------------------------------- doors

  /** Units sold from a door's lot since it was last full (resets `restockS` after the first sale). */
  private doorSold(p: TradePlace, now: number): Goods {
    const d = this.s.doors[p.id];
    if (!d || now - d.since >= TRADE.restockS) return {};
    return d.sold;
  }

  /** Left on the table at a door: the lot, plus a tip's extra lot. */
  doorLeft(placeId: string, g: TradeGood, now: number): number {
    const p = tradePlace(placeId);
    const stock = p?.door?.[g];
    if (!p || !stock) return 0;
    const tip = this.tipAt('lot', p.id, g, now);
    return Math.max(0, stock.lot - (this.doorSold(p, now)[g] ?? 0)) + (tip ? tip.qty - tip.used : 0);
  }

  /** Seconds until a door's lot is back (0: full now). */
  doorBackIn(placeId: string, now: number): number {
    const d = this.s.doors[placeId];
    if (!d || isEmpty(d.sold)) return 0;
    return Math.max(0, Math.ceil(d.since + TRADE.restockS - now));
  }

  buyQuote(placeId: string, g: TradeGood, qty: number, now: number): Quote {
    const p = tradePlace(placeId);
    const stock = p?.door?.[g];
    if (!p || !stock) return { ok: false, total: 0, unit: 0, qty, reason: 'Not sold here.' };
    const tip = this.tipAt('lot', p.id, g, now);
    const cheap = tip ? Math.min(qty, tip.qty - tip.used) : 0;
    const pressure = this.doorSold(p, now)[g] ?? 0;
    const a = cheap > 0 ? quotePurchase({ baseCost: stock.cost * tip!.mul, defId: g, qty: cheap, pressure: 0 }) : { total: 0, unitFirst: 0 };
    const b = qty - cheap > 0 ? quotePurchase({ baseCost: stock.cost, defId: g, qty: qty - cheap, pressure }) : { total: 0, unitFirst: 0 };
    return { ok: true, total: a.total + b.total, unit: cheap > 0 ? a.unitFirst : b.unitFirst, qty };
  }

  /** Most you could buy here right now (lot, room where it goes, cash). */
  maxBuy(placeId: string, g: TradeGood, now: number, car: CarRef | null, holdall: boolean, cash: number): number {
    let n = Math.min(this.doorLeft(placeId, g, now), this.room(g, car, holdall));
    while (n > 0 && this.buyQuote(placeId, g, n, now).total > cash) n--;
    return n;
  }

  /** Buy `qty`: checks lot, room and cash (`cash` is what you have); returns the quote with ok false and a reason when not. */
  buy(placeId: string, g: TradeGood, qty: number, now: number, car: CarRef | null, holdall: boolean, cash: number): Quote {
    const q = this.buyQuote(placeId, g, qty, now);
    if (!q.ok) return q;
    if (qty < 1) return { ...q, ok: false, reason: 'Nothing to buy.' };
    if (this.doorLeft(placeId, g, now) < qty) return { ...q, ok: false, reason: 'They haven’t got that many. More later.' };
    if (this.room(g, car, holdall) < qty) return { ...q, ok: false, reason: car ? `No room in the boot of the ${car.name}.` : goodMaxOnFoot(g) <= (this.s.pockets[g] ?? 0) ? `You can’t carry more ${GOOD_LABEL[g].units} on foot — bring a car.` : 'Your pockets are full.' };
    if (q.total > cash) return { ...q, ok: false, reason: 'Not enough cash.' };
    const p = tradePlace(placeId)!;
    // Units from a lot tip first, then the regular lot.
    const tip = this.tipAt('lot', p.id, g, now);
    const cheap = tip ? Math.min(qty, tip.qty - tip.used) : 0;
    if (tip) tip.used += cheap;
    if (qty - cheap > 0) {
      let d = this.s.doors[p.id];
      if (!d || now - d.since >= TRADE.restockS) d = this.s.doors[p.id] = { since: now, sold: {} };
      add(d.sold, g, qty - cheap);
    }
    this.addHeld(g, qty, q.total, car ? this.boot(car)! : this.s.pockets);
    return q;
  }

  /** Take goods in at `cost` total (a purchase, the runner's load at 0): `paid` becomes the new average. */
  addHeld(g: TradeGood, qty: number, cost: number, into: Goods): void {
    const before = this.held(g);
    const avg = this.s.paid[g] ?? 0;
    this.s.paid[g] = Math.round(((avg * before + cost) / Math.max(1, before + qty)) * 100) / 100;
    add(into, g, qty);
  }

  // ----------------------------------------------------------------------------------------- buyers

  sellQuote(placeId: string, g: TradeGood, qty: number, now: number): SaleQuote {
    const p = tradePlace(placeId);
    const none: SaleQuote = { ok: false, total: 0, unit: 0, qty, profit: 0, capped: 0, heat: 0, risk: dealRisk(0), tip: null };
    if (!p?.buyer || !p.buyer.accepts.includes(g)) return { ...none, reason: 'Not wanted here.' };
    if (qty < 1) return { ...none, reason: 'Nothing to sell.' };
    const q = quoteSale({ defId: g, quality: 0, qty, buyerType: p.buyer.type, district: p.district, pressure: this.market.pressure(p.district, g), districtHeat: this.market.heat.get(p.district) ?? 0 });
    const tip = this.tipAt('buyer', p.id, g, now);
    const mul = tip ? tip.mul : 1;
    let total = Math.floor(q.total * mul);
    const cost = (this.s.paid[g] ?? 0) * qty;
    const margin = total - cost;
    const room = Math.max(0, TRADE.hourCap - this.capUsed(now));
    let capped = 0;
    if (margin > room) {
      const paidOut = Math.floor(cost + room + (margin - room) * TRADE.overCapShare);
      capped = total - paidOut;
      total = paidOut;
    }
    return { ok: true, total, unit: Math.floor(q.unitFirst * mul), qty, profit: Math.round(total - cost), capped, heat: q.heat, risk: dealRisk(q.heat), tip };
  }

  /** Most one sale here takes: what you carry, and the buyer's limit (a street buyer takes 3). */
  maxSell(placeId: string, g: TradeGood, car: CarRef | null): number {
    const p = tradePlace(placeId);
    if (!p?.buyer || !p.buyer.accepts.includes(g)) return 0;
    return Math.min(this.carried(g, car), BUYER_TYPES[p.buyer.type].maxQty);
  }

  /** Sell `qty` (from the boot first, then your pockets). The caller pays out `total` and rolls the risk. */
  sell(placeId: string, g: TradeGood, qty: number, now: number, car: CarRef | null): SaleQuote {
    const q = this.sellQuote(placeId, g, qty, now);
    if (!q.ok) return q;
    if (this.maxSell(placeId, g, car) < qty) return { ...q, ok: false, reason: this.carried(g, car) < qty ? `You haven’t got ${qty} with you.` : 'They won’t take that many at once.' };
    const p = tradePlace(placeId)!;
    let left = qty;
    const boot = car ? this.boots.get(car.id) : undefined;
    if (boot) {
      const n = Math.min(left, boot[g] ?? 0);
      add(boot, g, -n);
      left -= n;
    }
    add(this.s.pockets, g, -left);
    if (this.held(g) === 0) delete this.s.paid[g];
    this.market.sold(p.district, g, qty, q.heat);
    this.s.ledger.push([now, q.profit]);
    this.s.ledger = this.s.ledger.filter(([t]) => t > now - TRADE.windowS);
    this.s.stats.deals++;
    this.s.stats.profit += q.profit;
    this.s.stats.best = Math.max(this.s.stats.best, q.profit);
    return q;
  }

  // ----------------------------------------------------------------------------------------- stash & losses

  /** Put everything you carry (pockets and this car's boot) in the stash, as far as it holds. */
  stashAll(car: CarRef | null): Goods {
    const room = () => STASH_CAPACITY - units(this.s.stash);
    const moved: Goods = {};
    const boot = car ? this.boots.get(car.id) : undefined;
    for (const from of [boot, this.s.pockets]) {
      if (!from) continue;
      const m = pour(from, this.s.stash, room());
      for (const k of TRADE_GOODS) if (m[k]) add(moved, k, m[k]!);
    }
    return moved;
  }

  /** Take `qty` of a good out of the stash into the boot (in a car) or your pockets. */
  takeOut(g: TradeGood, qty: number, car: CarRef | null, holdall: boolean): number {
    const n = Math.min(qty, this.s.stash[g] ?? 0, this.room(g, car, holdall));
    if (n <= 0) return 0;
    add(this.s.stash, g, -n);
    add(car ? this.boot(car)! : this.s.pockets, g, n);
    return n;
  }

  /** Nicked (pockets and the boot of the car you were in) or laid out (pockets): what was lost. */
  seize(which: 'busted' | 'wasted', car: CarRef | null): Goods {
    const lost: Goods = {};
    pour(this.s.pockets, lost);
    if (which === 'busted' && car) {
      const b = this.boots.get(car.id);
      if (b) pour(b, lost);
    }
    this.forgetEmpty(lost);
    return lost;
  }

  /** A loaded car is gone (wrecked, towed, out of the world): its boot goes with it. */
  loseBoot(carId: number): Goods {
    const b = this.boots.get(carId);
    this.boots.delete(carId);
    const lost: Goods = {};
    if (b) pour(b, lost);
    this.forgetEmpty(lost);
    return lost;
  }

  private forgetEmpty(lost: Goods): void {
    this.s.stats.lost += count(lost);
    for (const k of TRADE_GOODS) if (lost[k] && this.held(k) === 0) delete this.s.paid[k];
  }

  /** Remember the prices at a place (its card was opened): the book's "prices you've heard". */
  hear(placeId: string, now: number): void {
    const p = tradePlace(placeId);
    if (!p) return;
    const buy: Goods = {};
    const sell: Goods = {};
    if (p.door) for (const g of Object.keys(p.door) as TradeGood[]) buy[g] = this.buyQuote(p.id, g, 1, now).unit;
    if (p.buyer) for (const g of p.buyer.accepts) sell[g] = this.sellQuote(p.id, g, 1, now).unit;
    this.s.heard[p.id] = { t: now, buy, sell };
  }
}

/** The Vanes' runner's load, rolled when you take the van: sometimes nothing, usually two goods, never more than the van holds. */
export function rollRunnerLoad(rng: Rng, capacity: number): Goods {
  if (rng.chance(TRADE.runnerEmpty)) return {};
  const pool: [TradeGood, number, number][] = [
    ['velvet', 2, 4],
    ['cut-spirits', 3, 6],
    ['hooky-cigs', 8, 16],
    ['ember-gin', 2, 5],
    ['blue-tins', 10, 24],
  ];
  const first = rng.int(0, pool.length - 1);
  const second = (first + rng.int(1, pool.length - 1)) % pool.length;
  const load: Goods = {};
  let room = capacity;
  for (const i of [first, second]) {
    const [g, lo, hi] = pool[i]!;
    const n = Math.min(rng.int(lo, hi), Math.floor(room / goodSize(g)));
    if (n > 0) {
      load[g] = n;
      room -= n * goodSize(g);
    }
  }
  return load;
}
